import styled from 'styled-components';

import { HtmlEditor } from 'modules/graphql/types';
import { BUTTON_SIZES, PrimaryButton } from 'ui/button';
import { ChevronLeftSmall } from 'ui/icons';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { body1 } from 'ui/theme';
import { prefixWithUserProject } from 'utils/indexPageUtils';

import { useLocalStorage } from 'utils/hooks/useLocalStorage';

import { ModalType, useModalContext } from 'modules/templateEditor/context';

import { displayContent } from 'modules/templateEditor/common';
import { EditorLoader } from 'modules/templateEditor/common/EditorLoader';
import { ContentTemplateSelector } from './ContentTemplateSelector';
import { SelectedContentEditorStep, useSelectContentEditorContext } from './contexts';

const BackButton = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const StyledChevronLeftSmall = styled(ChevronLeftSmall)`
  margin-right: ${({ theme }) => theme.spacing.values.half};
  path {
    stroke: #717171;
    stroke-width: 0.75;
  }
`;

const Description = styled.span`
  ${body1}
  text-align: left;
`;

export const SELECT_CONTENT_TEMPLATE_MODAL_TEST_ID = 'content-template-selector-modal';
export const SELECT_CONTENT_TEMPLATE_SELECT_BUTTON_TEST_ID =
  'content-template-selector-select-button';

export const SelectContentTemplateModal = () => {
  const [selectedEditor] = useLocalStorage(
    prefixWithUserProject('template-edit-default-content-editor'),
    '' as HtmlEditor,
  );
  const [selectedTemplate, setSelectedTemplate] = useLocalStorage(
    prefixWithUserProject('template-edit-default-dragDrop-template'),
    '',
  );
  const { isLoadingTemplates, onSelectEditor, setSelectedContentEditorStep } =
    useSelectContentEditorContext();

  const { closeModal } = useModalContext();

  const handleCloseModal = () => {
    closeModal(ModalType.SelectContentEditorModals);
  };

  const mainButtonLabel = displayContent.btnSelect;
  const mainButtonIsDisabled = !selectedTemplate;

  return (
    <Modal
      data-test={SELECT_CONTENT_TEMPLATE_MODAL_TEST_ID}
      id={`contentTemplateSelectorModal`}
      isModalOpen={true}
      onClose={handleCloseModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>
        <BackButton
          onClick={() => setSelectedContentEditorStep(SelectedContentEditorStep.SelectEditor)}
        >
          <StyledChevronLeftSmall height={36} width={36} />
          {displayContent.btnBack}
        </BackButton>
      </ModalHeader>
      <ModalBody>
        <Description>{displayContent.strSelectTemplateInfo}</Description>
        {isLoadingTemplates ? (
          <EditorLoader />
        ) : (
          <ContentTemplateSelector
            setSelectedTemplate={setSelectedTemplate}
            selectedTemplate={selectedTemplate}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <PrimaryButton
          onClick={() => onSelectEditor(selectedEditor, selectedTemplate)}
          isDisabled={mainButtonIsDisabled}
          data-test={SELECT_CONTENT_TEMPLATE_SELECT_BUTTON_TEST_ID}
          size={BUTTON_SIZES.LARGE}
        >
          <span>{mainButtonLabel}</span>
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
